import { useState } from 'react';
import { ArrowUpDown, Check, ChevronDown } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { MobileFilterPillButton } from './MobileFilterPillButton';

export interface SortOption<V extends string = string> {
  value: V;
  label: string;
}

interface MobileSortPillProps<V extends string = string> {
  value: V;
  options: SortOption<V>[];
  /** Sort value treated as "default" — pill stays neutral instead of going gold. */
  defaultValue?: V;
  onChange: (value: V) => void;
  title?: string;
}

/**
 * Mobile Sort pill — same pill spec as MobileFilterPillButton, opens a bottom
 * sheet listing sort options with a check mark next to the active one.
 */
export function MobileSortPill<V extends string = string>({
  value,
  options,
  defaultValue,
  onChange,
  title = 'Sort by',
}: MobileSortPillProps<V>) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  const isActive = defaultValue !== undefined ? value !== defaultValue : false;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <MobileFilterPillButton
          label="Sort"
          icon={ArrowUpDown}
          trailingIcon={ChevronDown}
          active={isActive}
          aria-label="Sort"
        />
      </SheetTrigger>
      <SheetContent side="bottom" className="bg-chrome border-white/[0.08] pt-2 gap-0">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-col gap-2 pb-2">
          {options.map((o) => {
            const active = value === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  'flex items-center justify-between min-h-12 px-4 py-3 rounded-xl text-base font-medium text-left',
                  'border transition-colors active:scale-[0.99]',
                  active
                    ? 'bg-active-gold/10 border-active-gold/40 text-active-gold'
                    : 'bg-white/[0.04] border-white/[0.06] text-foreground hover:bg-white/[0.07] active:bg-white/[0.08]',
                )}
              >
                <span>{o.label}</span>
                {active && <Check className="h-5 w-5 shrink-0" />}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
